import { useEffect, useState, useRef } from "react";
import Image1 from "../../../../assets/business/images/total-redemptions-img.png";
import { Link, NavLink } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { UserGraphData } from "../../services/reports.services";
import { startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";

export default function UserReports() {
  const [openPopupIndex, setOpenPopupIndex] = useState(null);
  const popupRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({data : [] , total : 0});
  const [chartData, setChartData] = useState([]);
  const [filters, setFilters] = useState({ name: '', date: '' , rangeType : 'year' });
  const now = new Date();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const [debouncedName, setDebouncedName] = useState(filters.name);

  // Helper function to get month name
  const getMonthName = (monthNumber) => {
  
    return months[monthNumber - 1];
  };

  // Helper function to handle Y-axis ticks for empty data
  const getYAxisTicks = (data, dataKey, defaultMax = 100) => {
    if (!data || data.length === 0) {
      // Return default ticks when no data
      return {
        ticks: [0, 20, 40, 60, 80, 100],
        max: 100,
      };
    }

    const maxValue = Math.max(...data.map((d) => d[dataKey]), 0);
    const step = Math.ceil(maxValue / 5) || 20; // Fallback to 20 if maxValue is 0
    const yMax = step * 5;
    const ticks = Array.from({ length: 6 }, (_, i) => i * step);

    return { ticks, max: yMax };
  };

  // Generate empty chart data for display when no data is available
  const getEmptyChartData = () => {
    
    const currentDate = new Date();
    const emptyData = [];

    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      emptyData.push({
        name: months[date.getMonth()],
        fullName: `${months[date.getMonth()]} ${date.getFullYear()}`,
        monthYear: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`,
        month: date.getMonth() + 1,
        year: date.getFullYear(),
      });
    }

    return emptyData;
  };

  // Simplified: Backend already sends last 12 months, just format for charts
  const processMonthlyStats = (monthlyStats) => {
    if (!monthlyStats || !Array.isArray(monthlyStats)) return [];
   

    return monthlyStats.map((stat) => ({
      name: filters.rangeType==='year' ?getMonthName(stat.month):`${stat.day}${getMonthName(stat.month)}`, 
      fullName: `${getMonthName(stat.month)} ${stat.year}`, // Full name for tooltip
      monthYear: stat.monthKey,
      userCount: parseFloat(stat.userCount?.toFixed(2) || 0), 
      month: stat.month,
      year: stat.year,
    }));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedName(filters.name);
    }, 500); // 500ms delay, adjust as needed

    // Cleanup timeout if input changes before 500ms
    return () => clearTimeout(handler);
  }, [filters.name]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await UserGraphData({...filters});
        const data = response?.data;
        // console.log("Dashboard Data:", data);

        if (data) {
          setDashboardData(data.userData);
          setLoading(false);

          // Process chart data - backend already provides filtered last 12 months
          const processedChartData = processMonthlyStats(data.monthlyUserStats);
          setChartData(processedChartData);
        }
      } catch (error) {
        console.error("Failed to fetch dashboard:", error);
      }
    };

    if (debouncedName !== undefined) {
      fetchData(); // your API call function
    }
  }, [ filters.rangeType , filters.date , debouncedName]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setOpenPopupIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

 

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      const formattedDate = dataPoint.fullName;

      return (
        <div className="bg-white p-3 border rounded shadow">
          <p className="font-medium">{formattedDate}</p>
          {payload.map((entry, index) => (
            <div key={index} style={{ color: "#6A6A6A" }}>
              <span>Customer Count: {entry.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };


  // Get chart data for display (use empty data if no real data)
  const displayChartData = chartData.length > 0 ? chartData : getEmptyChartData();

  // Get Y-axis configurations
  const valueAxisConfig = getYAxisTicks(chartData, "userCount");

  const resetFilter = ()=>{

    setFilters({
      name: '', date: '' , rangeType : 'year'
    })
  }

 



  

  return (
    <div className="main-content">
      <div className="deshboard-part">
        <div className="report-part">
          <ul className="nav custom-tab-buttons" role="tablist">
            <li className="nav-item">
              <NavLink to="/business/reports" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                Transactions
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink to="/business/user-reports" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                Customers
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink to="/business/offline-transaction-reports" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                Offline Payments
              </NavLink>
            </li>
          </ul>
        </div>

        <div class="card-box d-flex flex-wrap align-items-center gap-2 my-3">
          <div class="search-bar">
            <div class="search-input">
            <input type="text" className="form-control" placeholder="Search by name" value={filters?.name||""} onChange={(e) => setFilters({ ...filters, name: e.target.value })} />
              <i class="fi fi-rs-search"></i>
            </div>
          </div>
          <div class="dropdown custom-dropdown">
            <button class="btn dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
              By {filters.rangeType} <i class="fi fi-rr-angle-small-down"></i>
            </button>
            <ul class="dropdown-menu">
              <li>
                <a
                  className="dropdown-item"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setFilters({
                      rangeType: "month",
                      startDate: startOfMonth(now).toISOString(),
                      endDate: endOfMonth(now).toISOString(),
                    });
                  }}
                >
                  Monthly
                </a>
              </li>

              <li>
                <a
                  className="dropdown-item"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setFilters({
                      rangeType: "year",
                      startDate: startOfYear(now).toISOString(),
                      endDate: endOfYear(now).toISOString(),
                    });
                  }}
                >
                  Yearly
                </a>
              </li>
              <li>
                <button class="dropdown-item" data-bs-toggle="modal" data-bs-target="#exampleModal">
                  Custom Range
                </button>
              </li>
            </ul>
          </div>
          <button class="btn btn-oring" onClick={()=>resetFilter()}>Reset Filter</button>
        </div>
        <div className="row g-3">
          <div className="col-md-6">
            <div className="card-box-1">
              <div className="left-box-1">
                <p className="body-1">Total Users</p>
                <h3 className="h3-headline">{dashboardData?.total||0}</h3>
              </div>
              <figure>
                <img src={Image1} alt="Total Redemptions" />
              </figure>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="row mt-4">
          <div className="graph-box">
            <h6 className="custem">Last 12 Months</h6>
            <div className="d-flex justify-content-between mb-3">
              {/* Value Redeemed Chart */}
              <div className="card-box w-100 d-flex flex-column">
                <h6 className="body-havey">Total Users</h6>
           
                <div style={{ flex: 1, marginLeft: "-1rem", marginTop: "1rem" }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={displayChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" height={40} />
                      <YAxis ticks={valueAxisConfig.ticks} domain={[0, valueAxisConfig.max]} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line type="monotone" dataKey="userCount" stroke="#F0913A" strokeWidth={2} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Redeemed Table */}
        <div className="card-box mt-4">
          <div className="d-flex justify-content-between mb-3">
            <h6 className="custem">Users</h6>
            <Link to={"/business/users/1"} className="text-decoration-none custem">
              View All
            </Link>
          </div>
          {/* Transactions Table */}
          <div className="table-box table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  {/* <th>Customer Id</th> */}
                  <th>Customer Name</th>
                  <th>Phone</th>
                  <th>Total Transactions</th>
                  <th>Created Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8">Loading...</td>
                  </tr>
                ) : dashboardData.data.length === 0 ? (
                  <tr className="text-center">
                    <td className="m-2" colSpan="8">
                      No Users found
                    </td>
                  </tr>
                ) : (
                  dashboardData.data.map((txn, index) => (
                    <tr key={index}>
                      <td>{txn?.fullName || txn?.name || txn?.email || "N/A"}</td>
                      <td>{txn?.phone?.fullNumber || txn?.phone?.phone || "N/A"}</td>
                      <td>{txn?.totalTransactions || "N/A"}</td>
                      <td>
                        {new Date(txn.lastTransactionAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </td>

                      <td className="popup">
                        <button className="btn btn-sm mt-1" onClick={() => setOpenPopupIndex(index)}>
                          <Link to={`/business/users/view/${txn._id}`}>
                            {" "}
                            <i className="fi fi-rr-eye" />
                          </Link>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="modal fade" id="exampleModal" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog">
        <div className="modal-content">
    <div className="modal-header">
      <h1 className="modal-title" id="exampleModalLabel">
        Filter by Custom Range
      </h1>
      <button
        type="button"
        className="btn-close"
        data-bs-dismiss="modal"
        aria-label="Close"
      >
        <i className="fi fi-rr-cross-circle"></i>
      </button>
    </div>

    <div className="modal-body">
      <div className="row">
        <div className="col-md-6">
          <label className="form-label subtitle-1">From</label>
          <input
            type="date"
            className="form-control"
            placeholder="Select date"
            value={filters.date?.startDate || ""}
            onChange={(e) =>
              setFilters({
                ...filters,
                date: { ...filters.date, startDate: e.target.value },
              })
            }
          />
        </div>

        <div className="col-md-6">
          <label className="form-label subtitle-1">To</label>
          <input
            type="date"
            className="form-control w-100"
            placeholder="Date"
            value={filters.date?.endDate || ""}
            onChange={(e) =>
              setFilters({
                ...filters,
                date: { ...filters.date, endDate: e.target.value },
              })
            }
          />
        </div>
      </div>
    </div>

    <div className="modal-footer">
      <button
        type="button"
        className="btn btn-oring"
        onClick={() => {
          if (filters.date?.startDate && filters.date?.endDate) {
            setFilters({
              ...filters,
              rangeType: "range",
            });
          }
        }}
        data-bs-dismiss="modal"
      >
        Continue
      </button>
    </div>
  </div>
        </div>
      </div>
    </div>
  );
}
